import React, { useMemo, useState } from 'react';
import { Box, FormControl, FormLabel, Stack, CircularProgress, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Fuse from 'fuse.js';
import { useDispatch, useSelector } from 'react-redux';
import { useGetBrokerGroupUsersQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	NoRowsErrorOverlay,
	NoRowsOverlay,
} from '@/components/table/DataGridComponents/NoRowsOverlay';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import DataGridColumnReorderToolbar from '@/components/table/DataGridComponents/DataGridColumnReorderToolbar';
import SearchField from '@/components/SearchField';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';
import { columns as dataColumns } from './data';
import { mapBrokerageUserRow, USERS_FUSE_OPTIONS } from './helpers';
import { UserRoleSelect } from './UserRoleSelect';
import { openDialog } from '@/features/dialog/dialogSlice';
// import { UserProfileDrawer } from '@/components/Drawers/UserProfileDrawer';

export const BrokerageUsersTable = () => (
	<ColumnControlContextProvider variant='brokerageUsers' value='list'>
		<BrokerageUsersTableContent />
	</ColumnControlContextProvider>
);

const BrokerageUsersTableContent = () => {
	const theme = useTheme();
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const [searchValue, setSearchValue] = useState('');
	const [roleFilter, setRoleFilter] = useState('');
	// const [selectedUser, setSelectedUser] = useState(null);

	const { data: userDetails, isLoading: isLoadingUser } = useGetUserDetailsQuery(
		{ email },
		{ skip: !email },
	);

	const {
		data: brokerGroupUsers = [],
		isLoading: isLoadingUsers,
		isError,
	} = useGetBrokerGroupUsersQuery(
		{
			userId: userDetails?.userId,
			showGroup: Boolean(userDetails?.canViewGroup),
		},
		{ skip: !userDetails?.userId },
	);

	const rows = useMemo(
		() => (brokerGroupUsers ?? []).map(mapBrokerageUserRow),
		[brokerGroupUsers],
	);

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(USERS_FUSE_OPTIONS.keys, rows);
		return new Fuse(rows, USERS_FUSE_OPTIONS, fuseIndex);
	}, [rows]);

	const filteredRows = useMemo(() => {
		const searchedRows = searchValue.trim()
			? searchFuse.search(searchValue).map((result) => result.item)
			: rows;

		if (!roleFilter) return searchedRows;
		return searchedRows.filter((row) => row.role === roleFilter);
	}, [rows, searchFuse, searchValue, roleFilter]);

	const { columns: orderedColumns, columnVisibilityModel, onColumnVisibilityModelChange, onColumnWidthChange } =
		useDataGridColumnProps(dataColumns);

	const isLoading = isLoadingUser || isLoadingUsers;

	if (isLoadingUser && !userDetails) {
		return <CircularProgress />;
	}

	return (
		<Box sx={{ width: '100%' }}>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={2}
				alignItems={{ xs: 'stretch', sm: 'flex-end' }}
				sx={{ mb: 2 }}
			>
				<FormControl sx={{ width: { xs: '100%', sm: 320 } }}>
					<FormLabel sx={{ mb: 0.5, lineHeight: '1.5', display: 'block' }}>
						Search
					</FormLabel>
					<SearchField
						value={searchValue}
						onChange={(event) => setSearchValue(event.target.value)}
						onReset={() => setSearchValue('')}
						helperText=''
					/>
				</FormControl>
				<FormControl sx={{ width: { xs: '100%', sm: 220 } }}>
					<FormLabel sx={{ mb: 0.5, lineHeight: '1.5', display: 'block' }}>
						Role
					</FormLabel>
					<UserRoleSelect
						value={roleFilter}
						onChange={(event) => setRoleFilter(event.target.value)}
					/>
				</FormControl>
			</Stack>

			<Box
				sx={{
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: '8px',
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<DataGrid
					initialState={{
						sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
					}}
					loading={isLoading}
					rows={filteredRows}
					columns={orderedColumns}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={onColumnVisibilityModelChange}
					onColumnWidthChange={onColumnWidthChange}
					disableRowSelectionOnClick
					hideFooterPagination
					hideFooter
					disableColumnFilter
					onRowClick={({ row }) => {
						if (!row?.id) return;
						dispatch(
							openDialog({
								dialogName: 'userProfile',
								data: { userId: row.id },
							}),
						);
					}}
					sx={{
						border: 0,
						borderRadius: '8px',
						'& .MuiDataGrid-cell': { outline: 'none' },
						'& .MuiDataGrid-row': { cursor: 'pointer' },
						'--DataGrid-overlayHeight': '3em',
					}}
					slots={{
						noRowsOverlay: isError
							? () => <NoRowsErrorOverlay objectType='users' />
							: () => <NoRowsOverlay objectType='users' />,
						noResultsOverlay: isError
							? () => <NoRowsErrorOverlay objectType='users' />
							: () => <NoRowsOverlay objectType='users' />,
						toolbar: DataGridColumnReorderToolbar,
						columnMenu: DataGridColumnMenu,
					}}
					slotProps={{
						loadingOverlay: {
							variant: 'skeleton',
							noRowsVariant: 'skeleton',
						},
					}}
				/>
			</Box>
		</Box>
	);
};

export default BrokerageUsersTable;
